// Client-safe media URL helper.
//
// DB stores object KEYS (private R2). A <img/audio/video src> or CSS
// background must never be a raw key. `mediaSrc()` maps a stored value to a
// STABLE same-origin proxy URL (`/api/media/proxy?key=…`) that 302-redirects
// to a freshly-signed presigned URL on every request — so nothing short-lived
// is baked at render, and expiry self-heals (the next load re-signs). Auth +
// IDOR are enforced by the proxy route.
//
// Passes through untouched:
//   - data:/blob: URIs
//   - already-proxied `/api/media/...` URLs
//   - public/CDN and third-party URLs (e.g. cdn.owlcat.club, Google avatars) —
//     these are already servable and need no signing.
//
// Accepts a bare KEY or a legacy Azure/R2 URL (the proxy resolves either).

const PRIVATE_STORAGE_HOST_SUFFIXES = ['.blob.core.windows.net', '.r2.cloudflarestorage.com'];

/** True when `ref` points at a PRIVATE object that must be signed to serve. */
export function isPrivateStorageRef(ref?: string | null): boolean {
  if (!ref) return false;
  if (ref.startsWith('data:') || ref.startsWith('blob:')) return false;
  // Any root-relative path is an already-servable app/public asset (the media
  // proxy itself, or static files like /characters/owl.png) — never a private
  // key. Bare object keys are relative WITHOUT a leading slash (story-audio/…).
  if (ref.startsWith('/')) return false;
  if (!/^https?:\/\//i.test(ref)) return true; // bare key
  try {
    const host = new URL(ref).hostname.toLowerCase();
    return PRIVATE_STORAGE_HOST_SUFFIXES.some((s) => host.endsWith(s));
  } catch {
    return false;
  }
}

export interface MediaSrcOpts {
  /** Force a download (Content-Disposition: attachment) with this filename. */
  download?: boolean;
  filename?: string;
  /**
   * Route through the ADMIN proxy (`/api/media/admin/proxy`), authorized by
   * admin role instead of the family IDOR check — for admin tooling that views
   * OTHER families' private media. Never expose to non-admin UI.
   */
  admin?: boolean;
}

/** Servable URL for a stored media value (proxy for private, passthrough otherwise). */
export function mediaSrc(ref?: string | null, opts: MediaSrcOpts = {}): string {
  if (!ref) return '';
  if (!isPrivateStorageRef(ref)) return ref;

  const params = new URLSearchParams({ key: ref });
  if (opts.download) params.set('download', '1');
  if (opts.filename) params.set('filename', opts.filename);
  const base = opts.admin ? '/api/media/admin/proxy' : '/api/media/proxy';
  return `${base}?${params.toString()}`;
}

/**
 * Admin variant of {@link mediaSrc}: routes private objects through the
 * admin-role-authorized proxy so admin tooling can view any family's media.
 */
export function adminMediaSrc(ref?: string | null, opts: Omit<MediaSrcOpts, 'admin'> = {}): string {
  return mediaSrc(ref, { ...opts, admin: true });
}
